import * as React from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { actionRequiredService, GET_USER_ME, userService } from '@/api';
import {
	ActionRequired,
	ActionRequiredType,
	Instructions,
	UseActionRequiredActionsParams,
	UseActionRequiredActionsResult,
} from '@/interfaces';
import { canOpenInstructionsPreview } from '@/utils/instructionsPreview';
import {
	hasDocumentRequiredAction,
	qualifiesForVerificationPhase,
} from '@/utils/actionRequired';
import { generateId } from '@/utils';
import { useFirstClientCaseId } from '@/hooks/queries/useFirstClientCaseId';

export const useActionRequiredActions = ({
	actionRequired,
	numericalOrder,
	refetchers,
}: UseActionRequiredActionsParams): UseActionRequiredActionsResult => {
	const router = useRouter();
	const { type, actionId } = router.query;
	const { refetchActionRequired, refetchCase, refetchDocuments } =
		refetchers ?? {};

	const [actionRequiredId, setActionRequiredId] = React.useState<number>();
	const [actionRequiredSelected, setActionRequiredSelected] =
		React.useState<ActionRequired>();
	const [isModalFilesOpen, setIsModalFilesOpen] = React.useState(false);
	const [isModalFileInstructions, setIsModalFileInstructions] =
		React.useState(false);
	const [isModalFollowUpOpen, setIsModalFollowUpOpen] = React.useState(false);
	const [isModalAcceptSettlement, setIsModalAcceptSettlement] =
		React.useState(false);
	const [isModalAcceptOpt, setIsModalAcceptOpt] = React.useState(false);
	const [isModalReviewSignOpen, setIsModalReviewSignOpen] =
		React.useState(false);
	const [isModalRetainerOpen, setIsModalRetainerOpen] = React.useState(false);
	const [isModalFollowSurveyOpen, setIsModalFollowSurveyOpen] =
		React.useState(false);
	const [documentName, setDocumentName] = React.useState<string>('');

	const [actionRequiredList, setActionRequiredList] =
		React.useState<ActionRequired[]>();
	const [applicationIdSelected, setApplicationIdSelected] =
		React.useState<number>();

	const [isIdentityVerificationOpen, setIsIdentityVerificationOpen] =
		React.useState(false);
	const [pendingInstructions, setPendingInstructions] = React.useState<
		Instructions | undefined
	>();
	const [documentFlowFromVerification, setDocumentFlowFromVerification] =
		React.useState(false);
	const identityVerificationId = React.useRef(generateId()).current;

	const [isStandaloneVerificationOpen, setIsStandaloneVerificationOpen] =
		React.useState(false);
	const standaloneVerificationId = React.useRef(generateId()).current;

	const { data: userMe, refetch: refetchUserMe } = useQuery({
		queryKey: [GET_USER_ME],
		queryFn: () => userService.getUserMe(),
	});
	const {
		caseId,
		numericalOrder: firstCaseNumericalOrder,
		totalCases,
	} = useFirstClientCaseId();

	React.useEffect(() => {
		if (actionRequired) {
			setActionRequiredList([...actionRequired]);
		}
	}, [actionRequired]);

	React.useEffect(() => {
		if (type && actionId) {
			handleOptionToOpen(type as string, actionId as string);
		}
	}, [type, actionId]);

	const handleOptionToOpen = (type: string, actionReqId: string): void => {
		setActionRequiredId(Number(actionReqId));
		switch (type as string) {
			case 'accept-settlement':
				setIsModalAcceptSettlement(true);
				break;
			case 'opt':
				setIsModalAcceptOpt(true);
				break;
			default:
				break;
		}
	};

	const approvalActionRequired = async (actionReqId: number): Promise<void> => {
		if (actionReqId)
			await actionRequiredService.putApprovalAction(actionReqId, true);
	};

	const openDocumentFlow = (instructions?: Instructions): void => {
		if (canOpenInstructionsPreview(instructions)) {
			setIsModalFileInstructions(true);
		} else {
			setIsModalFilesOpen(true);
		}
	};

	const handleIdentityVerificationContinue = async (): Promise<void> => {
		try {
			await refetchUserMe();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(
				'[ActionRequired] refetchUserMe failed after verification',
				error
			);
		}
		setDocumentFlowFromVerification(true);
		openDocumentFlow(pendingInstructions);
	};

	const hasDocumentAction = hasDocumentRequiredAction(actionRequiredList);
	const visibleActionRequiredList = (actionRequiredList ?? []).filter(
		(item) => item.action_type !== 'COMPLETE_PROFILE'
	);

	const isEligibleForVerificationPhase = qualifiesForVerificationPhase(
		hasDocumentAction,
		numericalOrder ?? firstCaseNumericalOrder
	);

	const showVerifyIdentityAction =
		!!userMe?.data?.is_verified &&
		!!caseId &&
		userMe?.data?.identity_verification_passed !== true &&
		!hasDocumentAction &&
		isEligibleForVerificationPhase;

	const startIdentityVerification = (): void => {
		const documentAction = (actionRequiredList ?? []).find(
			(item) =>
				item.action_type === 'DOCUMENT' ||
				item.action_type === 'REQUESTED_DOCUMENT'
		);
		if (documentAction) {
			setActionRequiredId(documentAction.id);
			setApplicationIdSelected(
				documentAction.case?.application_id ??
					documentAction.application?.id ??
					documentAction.application_id
			);
			setActionRequiredSelected(documentAction);
			setDocumentName(documentAction.name);
			setPendingInstructions(documentAction.criteria?.instructions);
			setIsIdentityVerificationOpen(true);
		} else {
			setIsStandaloneVerificationOpen(true);
		}
	};

	const openStandaloneVerification = (): void => {
		setIsStandaloneVerificationOpen(true);
	};

	const handleStandaloneVerificationDone = async (): Promise<void> => {
		setIsStandaloneVerificationOpen(false);
		await refetchUserMe();
	};

	const selectActionRequired = (selected: ActionRequired): void => {
		setActionRequiredSelected(selected);
		setDocumentName(selected.name);
	};

	const handleClickOptionToOpen = async (
		type: ActionRequiredType,
		actionReqId: number,
		caseId?: string,
		appId?: number,
		url?: string,
		instructions?: Instructions
	): Promise<void> => {
		setActionRequiredId(actionReqId);
		setApplicationIdSelected(appId);

		switch (type) {
			case 'APPROVAL':
				setIsModalAcceptSettlement(true);
				break;
			case 'FORM':
				setIsModalFollowUpOpen(true);
				break;
			case 'DOCUMENT':
			case 'REQUESTED_DOCUMENT': {
				if (userMe?.data?.identity_verification_passed !== true) {
					setPendingInstructions(instructions);
					setIsIdentityVerificationOpen(true);
					break;
				}
				setDocumentFlowFromVerification(false);
				openDocumentFlow(instructions);
				break;
			}
			case 'RETAINER':
				setIsModalRetainerOpen(true);
				break;
			case 'OPT_OUT':
				setIsModalAcceptOpt(true);
				break;
			case 'SETTLEMENT_WITH_SIGN':
			case 'SETTLEMENT_WITHOUT_SIGN':
				setIsModalAcceptSettlement(true);
				break;
			case 'COMPLAINT':
				await approvalActionRequired(actionReqId);
				url && window.open(url, '_blank');
				break;
			case 'SIGNATURE':
				setIsModalReviewSignOpen(true);
				break;
			case 'FOLLOW_UP_DOCUMENT':
				setIsModalFollowSurveyOpen(true);
				break;
			default:
				break;
		}
	};

	const refetchUploadDocumentation = (): void => {
		refetchActionRequired && refetchActionRequired();
		refetchCase && refetchCase();
		refetchDocuments && refetchDocuments();
	};

	const refetchOptOut = (): void => {
		refetchCase && refetchCase();
		refetchActionRequired && refetchActionRequired();
		refetchDocuments && refetchDocuments();
	};

	const refetchActionRequiredOnly = (): void => {
		refetchActionRequired && refetchActionRequired();
	};

	const refetchActionAndDocuments = (): void => {
		refetchActionRequired && refetchActionRequired();
		refetchDocuments && refetchDocuments();
	};

	return {
		actionRequiredList,
		visibleActionRequiredList,
		hasDocumentAction,
		showVerifyIdentityAction,
		userMe,
		caseId,
		totalCases,
		actionRequiredId,
		actionRequiredSelected,
		applicationIdSelected,
		documentName,
		documentFlowFromVerification,
		identityVerificationId,
		standaloneVerificationId,
		isModalFilesOpen,
		setIsModalFilesOpen,
		isModalFileInstructions,
		setIsModalFileInstructions,
		isModalFollowUpOpen,
		setIsModalFollowUpOpen,
		isModalAcceptSettlement,
		setIsModalAcceptSettlement,
		isModalAcceptOpt,
		setIsModalAcceptOpt,
		isModalReviewSignOpen,
		setIsModalReviewSignOpen,
		isModalRetainerOpen,
		setIsModalRetainerOpen,
		isModalFollowSurveyOpen,
		setIsModalFollowSurveyOpen,
		isIdentityVerificationOpen,
		setIsIdentityVerificationOpen,
		isStandaloneVerificationOpen,
		setIsStandaloneVerificationOpen,
		refetchUserMe,
		handleClickOptionToOpen,
		selectActionRequired,
		handleIdentityVerificationContinue,
		handleStandaloneVerificationDone,
		openStandaloneVerification,
		startIdentityVerification,
		refetchUploadDocumentation,
		refetchOptOut,
		refetchActionRequiredOnly,
		refetchActionAndDocuments,
	};
};
